/**
 * AuditLogTab Component
 *
 * Story 9-14: Audit Log Viewer
 * View system audit logs for security and compliance.
 */

import { useState } from 'react';
import { useAuditLog } from '../useSettings';
import type { AuditLogQueryParams } from '../../../lib/settingsApi';

// Action type labels and colors
const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  create: { label: 'Aangemaakt', color: 'bg-green-100 text-green-800' },
  update: { label: 'Gewijzigd', color: 'bg-blue-100 text-blue-800' },
  delete: { label: 'Verwijderd', color: 'bg-red-100 text-red-800' },
  login: { label: 'Ingelogd', color: 'bg-purple-100 text-purple-800' },
  logout: { label: 'Uitgelogd', color: 'bg-gray-100 text-gray-800' },
  view: { label: 'Bekeken', color: 'bg-yellow-100 text-yellow-800' },
  export: { label: 'Geexporteerd', color: 'bg-indigo-100 text-indigo-800' },
};

// Entity labels
const ENTITY_LABELS: Record<string, string> = {
  user: 'Gebruiker',
  employee: 'Medewerker',
  evaluation: 'Beoordeling',
  department: 'Team',
  function: 'Functie',
  role: 'Rol',
  setting: 'Instelling',
  agreement: 'Afspraak',
  cycle: 'Cyclus',
};

export function AuditLogTab() {
  const [filters, setFilters] = useState<AuditLogQueryParams>({
    limit: 50,
    offset: 0,
  });
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const { data, isLoading, error, refetch } = useAuditLog(filters);

  const handleFilterChange = (key: keyof AuditLogQueryParams, value: string | number | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      offset: key !== 'offset' ? 0 : prev.offset, // Reset offset when changing filters
    }));
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatChanges = (oldValues: Record<string, unknown> | null, newValues: Record<string, unknown> | null) => {
    if (!oldValues && !newValues) return null;

    const changes: { field: string; oldValue: unknown; newValue: unknown }[] = [];
    const allKeys = new Set([
      ...Object.keys(oldValues || {}),
      ...Object.keys(newValues || {}),
    ]);

    allKeys.forEach((key) => {
      const oldVal = oldValues?.[key];
      const newVal = newValues?.[key];
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changes.push({ field: key, oldValue: oldVal, newValue: newVal });
      }
    });

    return changes;
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    const newOffset = direction === 'next'
      ? (filters.offset || 0) + (filters.limit || 50)
      : Math.max(0, (filters.offset || 0) - (filters.limit || 50));
    handleFilterChange('offset', newOffset);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Fout bij laden van audit log: {error.message}
      </div>
    );
  }

  const logs = data?.data || [];
  const pagination = data?.pagination || { total: 0, limit: 50, offset: 0 };
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Audit Log</h2>
          <p className="mt-1 text-sm text-gray-500">
            Bekijk alle systeemactiviteiten voor beveiliging en compliance
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Vernieuwen
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Actie</label>
            <select
              value={filters.action || ''}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Alle acties</option>
              {Object.entries(ACTION_LABELS).map(([value, { label }]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vanaf datum</label>
            <input
              type="date"
              value={filters.fromDate || ''}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tot datum</label>
            <input
              type="date"
              value={filters.toDate || ''}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Items per pagina</label>
            <select
              value={filters.limit || 50}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Audit Log Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        {logs.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tijdstip
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gebruiker
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actie
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Entiteit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.map((log) => {
                    const actionDef = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-800' };
                    const changes = formatChanges(log.oldValues, log.newValues);
                    const isExpanded = expandedId === log.id;

                    return (
                      <>
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(log.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {log.userName || `Gebruiker #${log.userId}`}
                            </div>
                            {log.ipAddress && (
                              <div className="text-xs text-gray-500">{log.ipAddress}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${actionDef.color}`}>
                              {actionDef.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {ENTITY_LABELS[log.entity] || log.entity}
                            {log.entityId && <span className="text-gray-500 ml-1">#{log.entityId}</span>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {changes && changes.length > 0 && (
                              <button
                                onClick={() => setExpandedId(isExpanded ? null : log.id)}
                                className="text-blue-600 hover:text-blue-800"
                              >
                                {isExpanded ? 'Verbergen' : `${changes.length} wijziging${changes.length > 1 ? 'en' : ''}`}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isExpanded && changes && (
                          <tr key={`${log.id}-details`}>
                            <td colSpan={5} className="px-6 py-4 bg-gray-50">
                              <div className="text-sm">
                                <h4 className="font-medium text-gray-900 mb-2">Wijzigingen:</h4>
                                <div className="space-y-2">
                                  {changes.map((change, idx) => (
                                    <div key={idx} className="flex gap-4">
                                      <span className="font-medium text-gray-700 min-w-32">{change.field}:</span>
                                      <span className="text-red-600 line-through">
                                        {JSON.stringify(change.oldValue) || '(leeg)'}
                                      </span>
                                      <span className="text-gray-400">→</span>
                                      <span className="text-green-600">
                                        {JSON.stringify(change.newValue) || '(leeg)'}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Toont <span className="font-medium">{pagination.offset + 1}</span> tot{' '}
                  <span className="font-medium">{Math.min(pagination.offset + pagination.limit, pagination.total)}</span> van{' '}
                  <span className="font-medium">{pagination.total}</span> resultaten
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange('prev')}
                    disabled={pagination.offset === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Vorige
                  </button>
                  <span className="text-sm text-gray-700">
                    Pagina {currentPage} van {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange('next')}
                    disabled={pagination.offset + pagination.limit >= pagination.total}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Volgende
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="px-6 py-12 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Geen log entries gevonden</h3>
            <p className="mt-1 text-sm text-gray-500">
              Er zijn geen audit log entries die aan de huidige filters voldoen.
            </p>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <svg className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-700">
            <p className="font-medium text-blue-800">Over de Audit Log</p>
            <ul className="mt-1 list-disc list-inside space-y-1">
              <li>Alle systeemwijzigingen worden automatisch gelogd</li>
              <li>Logs worden 2 jaar bewaard conform AVG-richtlijnen</li>
              <li>Exports zijn beschikbaar via het rapportagesysteem</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
